/* Libraries */
import React, {memo} from 'react';
import cn from 'classnames';
import dynamic from 'next/dynamic';

/* Hooks */
import {useIsSessionLoadedSelector} from '@store/queries/user/useUser.selector';

/* Types */
import {TCartFlow} from 'src/utils/cart/cartFlowFactory';

/* Components */
import {Tooltip, Icon} from 'ht-styleguide';
import PhoneDisplayLink, {ICON_ORIENTATION} from '@components/PhoneDisplayLink';
import CartPreviewIcon from '@components/PageLayouts/Header/Parts/CartPreviewIcon';
/* Styles */
import styles from '../standardheader.module.scss';

/* Dynamic Components */
const SearchBarDynamic = dynamic(() => import('@components/PageLayouts/Header/Standard/Parts/SearchBar'), {ssr: false});

/* Types Inline */
type RightComponentProps = {
  containerStyles: string;
  showSearchBar: boolean;
  toggleAccountMenu: BaseAnyFunction;
  themeStyles: string;
  onClose: BaseAnyFunction;
  searchClick: BaseAnyFunction;
  toggleCartMenu?: BaseAnyFunction | null;
  className?: string;
  standardHeaderConfig: ReturnType<TCartFlow['getStandardHeaderConfig']>;
};

const RightComponent = ({
  containerStyles,
  themeStyles,
  toggleAccountMenu,
  showSearchBar,
  onClose,
  searchClick,
  standardHeaderConfig,
  ...props
}: RightComponentProps) => {
  const phoneIcon = <Icon name="phone-outlined" className={styles.phoneIcon} />;
  /* Hooks */
  const sessionLoaded = useIsSessionLoadedSelector();

  /* Style */
  const phoneStyles = cn('p1 e2e-header-nav-phone', styles.clickToCall);
  const wrapperStyles = cn({
    [styles.rightContent]: !showSearchBar,
    [styles.rightContentSearch]: showSearchBar,
  });
  const searchStyles = !showSearchBar ? styles.switchSearchIcon : styles.search;

  const showPhoneDisplay = !showSearchBar;
  const showCartIcon = !showSearchBar && standardHeaderConfig?.showCartIcon && sessionLoaded;
  const showProfileIcon = !showSearchBar;

  return (
    <div className={wrapperStyles}>
      {showPhoneDisplay && (
        <div className={styles.clickToCallWrapper}>
          <Tooltip content="Customer Support">
            <PhoneDisplayLink linkStyle={phoneStyles} displayNumber icon={phoneIcon} iconOrientation={ICON_ORIENTATION.left} />
          </Tooltip>
        </div>
      )}
      <div className={searchStyles}>
        {showSearchBar ? (
          <SearchBarDynamic showSearchBar={showSearchBar} onClose={onClose} searchClick={searchClick} {...props} />
        ) : (
          <Icon name="search" onClick={searchClick} className={cn(styles.icon, props.className || {})} />
        )}
        {showCartIcon && (
          <CartPreviewIcon containerStyles={containerStyles} themeStyles={themeStyles} onClick={standardHeaderConfig.onCartIconClick} />
        )}
      </div>
      {showProfileIcon && <Icon name="profile-outlined" className={styles.profileIcon} onClick={toggleAccountMenu} dataTestId="nav-link-account" />}
    </div>
  );
};

export default memo(RightComponent);
